import { appIdentity } from './linkRoute';

export type DevicePlatform = 'ios' | 'android' | 'desktop';

interface NavigatorLike {
  userAgent: string;
  maxTouchPoints: number;
}

interface Nav {
  /** 폴백 웹 주소의 origin — 늘 지금 페이지의 것이다. */
  origin: string;
  assign: (url: string) => void;
  replace: (url: string) => void;
}

// 앱이 스킴을 가로채기까지 기다리는 시간. iOS Safari 는 스킴 이동을 ~300ms, Android Chrome 은
// 600~800ms 뒤에 넘긴다(epyt `openInApp` 주석 인용, 미검증) — 여유를 두되 기다린다고 느끼지 않을 만큼.
const FALLBACK_DELAY_MS = 1200;

/**
 * UA 로 기기를 가른다. iPadOS 13+ Safari 는 데스크톱(Macintosh) UA 를 보내므로 터치 지점으로
 * 다시 본다 — 데스크톱 Mac 은 `maxTouchPoints` 가 0 이다.
 */
export const detectPlatform = (
  nav: NavigatorLike = navigator,
): DevicePlatform => {
  const ua = nav.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) {
    return 'ios';
  }
  if (/android/i.test(ua)) {
    return 'android';
  }
  if (/macintosh/i.test(ua) && nav.maxTouchPoints > 1) {
    return 'ios';
  }
  return 'desktop';
};

// 대기 중인 폴백은 하나뿐이다 — 연달아 불러도 웹 이동이 겹치지 않는다.
let cancelPending: (() => void) | null = null;

const scheduleFallback = (nav: Nav, fallbackUrl: string): (() => void) => {
  cancelPending?.();
  // 탭이 한 번이라도 가려졌으면 앱이 열린 것이다. 타이머 시점의 `document.hidden` 만 보면
  // 앱을 열고 grace 안에 Safari 로 돌아온 사람을 웹으로 끌고 간다.
  let appOpened = false;
  const onVisibility = () => {
    if (document.hidden) {
      appOpened = true;
    }
  };
  const timer = setTimeout(() => {
    cancel();
    if (!appOpened && !document.hidden) {
      nav.replace(fallbackUrl);
    }
  }, FALLBACK_DELAY_MS);
  document.addEventListener('visibilitychange', onVisibility);
  const cancel = () => {
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibility);
    if (cancelPending === cancel) {
      cancelPending = null;
    }
  };
  cancelPending = cancel;
  return cancel;
};

const noop = () => undefined;

export interface OpenInAppOptions {
  /** 앱 안의 경로(`parseLinkTarget` 결과). 앱이 없으면 지금 origin 의 같은 경로로 간다. */
  path: string;
  /** 모바일만 — 데스크톱은 열 앱이 없어 호출자가 웹으로 보낸다. 테스트 주입점. */
  platform: Exclude<DevicePlatform, 'desktop'>;
  isProd?: boolean;
  nav?: Nav;
}

/**
 * 모바일 브라우저에서 앱으로 `path` 를 열어 보고, 앱이 없으면 지금 origin 의 같은 경로(웹)로 간다.
 * 스토어로 보내지 않는다 — 링크의 목적지(공유된 결과)를 웹에서 먼저 보여 주고, 스토어는 그 화면의
 * 버튼이 연다(`storeUrlOf`, `SharedResultPage`).
 *
 * - iOS: 커스텀 스킴으로 이동하고 grace 뒤에도 탭이 그대로면 웹으로. 앱이 없으면 Safari 가
 *   "주소가 유효하지 않음"을 한 번 띄운다 — 카카오톡 인앱 브라우저처럼 Universal Link 가
 *   안 걸리는 곳에서 앱을 여는 길이 이것뿐이라 감수한다.
 * - Android: `intent://` 에 폴백 URL 을 실어 Chrome 이 앱 부재를 스스로 처리하게 한다.
 *
 * 돌려주는 함수는 대기 중인 폴백을 끈다 — 사용자가 먼저 웹으로 가면 부른다. 안 끄면 1.2초 뒤
 * 같은 목적지를 한 번 더 불러온다.
 */
export const openInApp = ({
  path,
  platform,
  isProd,
  nav = window.location,
}: OpenInAppOptions): (() => void) => {
  const { scheme, androidPackage } = appIdentity(isProd);
  // 폴백을 호출자에게 받지 않고 여기서 만든다 — 다른 origin 으로 떨어뜨릴 길이 없다.
  const fallbackUrl = `${nav.origin}${path}`;
  if (platform === 'android') {
    // 해시는 `#Intent` 구분자와 겹치므로 뗀다. 첫 `#` 뒤 전부 — 정규식 `.` 는 줄바꿈을 건너지 못한다.
    const target = path.split('#', 1)[0].replace(/^\//, '');
    nav.assign(
      `intent://${target}#Intent;scheme=${scheme};package=${androidPackage};` +
        `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`,
    );
    return noop;
  }
  const cancel = scheduleFallback(nav, fallbackUrl);
  // `muzly-dev:/` + `/result/1` → `muzly-dev://result/1` — 앱의 `toRoutePath` 가 되돌리는 모양.
  nav.assign(`${scheme}:/${path}`);
  return cancel;
};
